import { notification } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { ROUTES } from '@/components/Shared/routes/routes';
import { SELECTED_BANK_STATUSES } from '@/constants';
import { handleError } from '@/services/handleError';
import { parseAutoCredLeasingResponse } from '@/services/mappers/autoCredMapper';
import { orderAppMapper } from '@/services/mappers/orderAppMapper';
import { dispatch, useAppSelector } from '@/store';
import {
  onSetSelectedBank,
  onSetSelectedLeasing
} from '@/store/financingStep.slice';
import {
  getAutoCredLeasingResponsesByRequestById,
  getRequestById,
  setCurrent,
  setShouldUpdateRequestByBiometricWs
} from '@/store/newOrder.slice';
import { getResponsesByRequestId } from '@/store/offerStep.slice';
import type { TAutoCredLeasingRequestApi, TNewOrder } from '@/types/newOrder';
import type { OfferResponseFromApi } from '@/types/offers';

import { mapBankOfferFromApi } from '../helpers/mapBankOfferFromApi';
import useAutoCredRequestId from './useAutoCredRequestId';

type GetRequestByIdFulfilled = ReturnType<typeof getRequestById.fulfilled>;

type BankResponsesAction =
  | ReturnType<typeof getResponsesByRequestId.fulfilled>
  | ReturnType<typeof getResponsesByRequestId.rejected>;

type LeasingResponsesAction =
  | ReturnType<typeof getAutoCredLeasingResponsesByRequestById.fulfilled>
  | ReturnType<typeof getAutoCredLeasingResponsesByRequestById.rejected>;

type ThunkActionResult = BankResponsesAction | LeasingResponsesAction;

const isRejected = (
  action: ThunkActionResult | null,
  matcher: (a: ThunkActionResult) => boolean
): boolean => action !== null && matcher(action);

const getPayload = <T>(action: ThunkActionResult | null): T | undefined =>
  action !== null && 'payload' in action
    ? (action as { payload: T }).payload
    : undefined;

const findFinancedIndex = (
  offers: { status?: { code?: string } }[] | undefined
) =>
  offers?.findIndex((o) =>
    SELECTED_BANK_STATUSES.includes(o?.status?.code ?? '')
  ) ?? -1;

const useRefreshOrderData = (
  options: {
    allowWithOrder?: boolean;
    autoFetch?: boolean;
    requestId?: string;
    skipRequestFetch?: boolean;
    skipResponsesFetch?: boolean;
  } = {}
) => {
  const navigate = useNavigate();

  const { dicts } = useAppSelector((state) => state.dict);
  const { orderApp } = useAppSelector((state) => state.newOrder);

  const [isLoading, setIsLoading] = useState(false);

  const { requestId: autoCredRequestId } = useAutoCredRequestId();

  const requestId = options.requestId || autoCredRequestId;

  const refreshOrderData = useCallback(
    async (
      reqId?: string,
      overrides?: {
        skipRequestFetch?: boolean;
        skipResponsesFetch?: boolean;
      }
    ) => {
      const skipRequestFetch: boolean =
        overrides?.skipRequestFetch ?? options.skipRequestFetch ?? false;
      const skipResponsesFetch: boolean =
        overrides?.skipResponsesFetch ?? options.skipResponsesFetch ?? false;

      const id = reqId || requestId;
      const skipDueToOrder = !options.allowWithOrder && orderApp?.uuid;

      if (!id || !dicts || skipDueToOrder) return;

      setIsLoading(true);

      const notifyAndRedirect = () => {
        notification.error({
          title: 'Не удалось загрузить заявку',
          description: 'Заявка либо не найдена, либо не доступна'
        });
        navigate(ROUTES.autoCredit);
      };

      try {
        let request: TNewOrder | null = orderApp;

        if (!skipRequestFetch) {
          const requestResponse = await dispatch(getRequestById({ reqId: id }));

          if (getRequestById.rejected.match(requestResponse)) {
            notifyAndRedirect();
            return;
          }

          request = orderAppMapper(
            (requestResponse as GetRequestByIdFulfilled).payload
          );
        }

        if (skipResponsesFetch) return;

        const requestSentTo = request?.sentTo;

        const hasBanks =
          requestSentTo === 'banks' || requestSentTo === 'banks_leasing';
        const hasLeasing =
          requestSentTo === 'leasing' || requestSentTo === 'banks_leasing';

        const fetches: Promise<ThunkActionResult>[] = [];
        if (hasBanks) {
          fetches.push(dispatch(getResponsesByRequestId({ reqId: id })));
        }
        if (hasLeasing) {
          fetches.push(
            dispatch(getAutoCredLeasingResponsesByRequestById({ reqId: id }))
          );
        }

        // banks_leasing:
        // [bankResponse, leasingResponse]

        // banks:
        // [bankResponse]

        // leasing:
        // [leasingResponse]

        const results = await Promise.all(fetches);
        const bankResult: BankResponsesAction | null = hasBanks
          ? (results[0] as BankResponsesAction)
          : null;
        const leasingResult: LeasingResponsesAction | null = hasLeasing
          ? (results[!hasBanks ? 0 : 1] as LeasingResponsesAction)
          : null;

        if (
          bankResult &&
          isRejected(bankResult, getResponsesByRequestId.rejected.match)
        ) {
          const errorMsg =
            'error' in bankResult ? bankResult.error?.message : undefined;
          if (errorMsg !== 'Unauthorized') {
            notifyAndRedirect();
            return;
          }
        }

        if (
          leasingResult &&
          isRejected(
            leasingResult,
            getAutoCredLeasingResponsesByRequestById.rejected.match
          )
        ) {
          notifyAndRedirect();
          return;
        }

        const bankPayload = getPayload<OfferResponseFromApi[]>(bankResult);
        const leasingPayload =
          getPayload<[TAutoCredLeasingRequestApi]>(leasingResult);
        const leasingResponses = leasingPayload?.[0]?.responses;

        const indexByBanks = findFinancedIndex(bankPayload);
        const indexByLeasing = findFinancedIndex(leasingResponses);

        const financedIndex =
          requestSentTo === 'banks'
            ? indexByBanks
            : requestSentTo === 'leasing'
              ? indexByLeasing
              : indexByBanks !== -1
                ? indexByBanks
                : indexByLeasing;

        const useBank =
          financedIndex !== -1 &&
          (requestSentTo === 'banks' ||
            (requestSentTo === 'banks_leasing' && indexByBanks !== -1));

        const useLeasing =
          financedIndex !== -1 &&
          (requestSentTo === 'leasing' ||
            (requestSentTo === 'banks_leasing' && indexByLeasing !== -1));

        if (request?.status === 'biometrics_failed') {
          dispatch(onSetSelectedBank(null));
          dispatch(onSetSelectedLeasing(null));
          dispatch(setCurrent(0));
        } else {
          if (useBank && bankPayload) {
            const financedOffer = mapBankOfferFromApi(
              bankPayload[financedIndex]
            );
            dispatch(onSetSelectedBank(financedOffer));
            dispatch(onSetSelectedLeasing(null));
            dispatch(setCurrent(2));
          } else if (useLeasing && leasingResponses) {
            const leasingOffer = leasingResponses[financedIndex];
            if (leasingOffer) {
              const financedOffer = parseAutoCredLeasingResponse(leasingOffer);
              dispatch(onSetSelectedBank(null));
              dispatch(onSetSelectedLeasing(financedOffer));
              dispatch(setCurrent(2));
            } else {
              dispatch(onSetSelectedBank(null));
              dispatch(onSetSelectedLeasing(null));
              dispatch(setCurrent(request?.status !== 'draft' ? 1 : 0));
            }
          } else {
            if (request?.status !== 'draft') {
              dispatch(onSetSelectedBank(null));
              dispatch(onSetSelectedLeasing(null));
            }
            dispatch(setCurrent(request?.status !== 'draft' ? 1 : 0));
          }
        }
      } catch (error) {
        handleError(error, {
          customTitle: 'Ошибка при получении данных заявки'
        });
      } finally {
        setIsLoading(false);
        if (!skipRequestFetch) {
          dispatch(setShouldUpdateRequestByBiometricWs(false));
        }
      }
    },
    [
      dicts,
      navigate,
      options.allowWithOrder,
      options.skipRequestFetch,
      options.skipResponsesFetch,
      orderApp,
      requestId
    ]
  );

  useEffect(() => {
    if (options.autoFetch !== false && requestId && dicts) {
      refreshOrderData();
    }
  }, [refreshOrderData, options.autoFetch, requestId, dicts]);

  return { refreshOrderData, isLoading };
};

export default useRefreshOrderData;
